import { apiClient } from "../shared/utils/apiClient";
import logger from "../shared/utils/logger";

/**
 * Activity Service
 *
 * Talks to the hybrid Recent Activity Feed endpoints:
 *  - GET /api/casino/activity/public   → public backbone ring buffer
 *  - GET /api/casino/activity/me/recent → current user's recent wins
 *
 * Both endpoints return an `activities` array shaped to drop straight
 * into SocialProofCard. See docs/features/20-HYBRID_RECENT_ACTIVITY_FEED.md
 * for the contract.
 *
 * All errors are caught and surfaced as empty arrays — the feed must
 * keep playing the fake generator if the backend is unreachable. The
 * caller does NOT need a try/catch around these helpers.
 */
class ActivityService {
  constructor() {
    this.baseUrl = "/api/casino/activity";
  }

  /**
   * Fetch the public backbone ring buffer (sampled real wins, masked).
   * No auth required.
   *
   * @returns {Promise<Array>} activities array; [] on failure
   */
  async getPublicActivity() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/public`);
      const activities = response?.data?.activities;
      return Array.isArray(activities) ? activities : [];
    } catch (error) {
      logger.warn(
        "ActivityService: getPublicActivity failed; backbone disabled this tick",
        error?.message || error,
      );
      return [];
    }
  }

  /**
   * Fetch the current user's recent wins for the YOU stream.
   * Requires the player JWT — should only be called when authenticated.
   *
   * @returns {Promise<Array>} activities array; [] on failure or no wins
   */
  async getMyRecentActivity() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/me/recent`);
      const activities = response?.data?.activities;
      return Array.isArray(activities) ? activities : [];
    } catch (error) {
      // 401 here is normal at edges (token rotation, just-signed-out) — keep
      // the log soft. The caller already gates this on isLoggedIn.
      logger.warn(
        "ActivityService: getMyRecentActivity failed; YOU stream disabled this mount",
        error?.message || error,
      );
      return [];
    }
  }
}

export const activityService = new ActivityService();
export default activityService;
